import { useEffect, useRef, useState } from "react";
import api from "../lib/api";

export default function OrderBook({ symbol, markPrice }) {
  const [book, setBook] = useState({ bids: [], asks: [] });
  const prevPriceRef = useRef(0);
  const [dir, setDir] = useState(0); // 1 up, -1 down, 0 flat

  useEffect(() => {
    if (!symbol) return;
    let alive = true;
    const load = () => api.get(`/market/depth?symbol=${symbol}&limit=15`).then((r) => alive && setBook(r.data)).catch(() => {});
    load();
    const i = setInterval(load, 1500);
    return () => { alive = false; clearInterval(i); };
  }, [symbol]);

  // Reset tick direction when the pair changes
  useEffect(() => { prevPriceRef.current = 0; setDir(0); }, [symbol]);

  // Track last-price tick direction for colouring
  useEffect(() => {
    if (!markPrice) return;
    const prev = prevPriceRef.current;
    if (prev && markPrice !== prev) setDir(markPrice > prev ? 1 : -1);
    prevPriceRef.current = markPrice;
  }, [markPrice]);

  const maxBid = Math.max(...(book.bids || []).map(([_, q]) => q), 1);
  const maxAsk = Math.max(...(book.asks || []).map(([_, q]) => q), 1);
  const mid = book.bids?.[0] && book.asks?.[0] ? (book.bids[0][0] + book.asks[0][0]) / 2 : 0;
  const fmt = (n, p) => Number(n).toFixed(p < 1 ? 4 : 2);
  const last = markPrice || mid;
  const lastColor = dir > 0 ? "text-[#0ECB81]" : dir < 0 ? "text-[#F6465D]" : "text-white";

  return (
    <div className="h-full flex flex-col bg-[#111827] border-l border-[#1F2937]" data-testid="order-book">
      <div className="px-3 py-2 border-b border-[#1F2937] flex items-center justify-between">
        <p className="overline">Order Book</p>
        <span className="text-[10px] text-[#9CA3AF] mono">Depth 15</span>
      </div>
      <div className="grid grid-cols-3 px-3 py-1 text-[10px] overline border-b border-[#1F2937]">
        <span>Price</span>
        <span className="text-right">Size</span>
        <span className="text-right">Sum</span>
      </div>

      {/* Asks (reverse so lowest at bottom) */}
      <div className="flex-1 overflow-hidden flex flex-col-reverse">
        {(book.asks || []).slice(0, 12).map(([price, qty], i) => {
          const sum = (book.asks || []).slice(0, i + 1).reduce((a, [, q]) => a + q, 0);
          const w = (qty / maxAsk) * 100;
          return (
            <div key={`ask-${i}`} className="relative grid grid-cols-3 px-3 py-0.5 text-[11px] mono">
              <div className="depth-bar-sell absolute inset-y-0 right-0" style={{ width: `${w}%` }} />
              <span className="relative text-[#F6465D]">{fmt(price, price)}</span>
              <span className="relative text-right">{qty.toFixed(4)}</span>
              <span className="relative text-right text-[#9CA3AF]">{sum.toFixed(3)}</span>
            </div>
          );
        })}
      </div>

      {/* Last price (matches chart & mark price) */}
      <div className="px-3 py-1.5 border-y border-[#1F2937] flex items-center justify-between bg-[#0A0E17]">
        <div className="flex items-baseline gap-1.5">
          <span className={`mono text-base font-semibold ${lastColor}`} data-testid="orderbook-last">{last > 0 ? fmt(last, last) : "—"}</span>
          {dir !== 0 && <span className={`text-[10px] ${lastColor}`}>{dir > 0 ? "▲" : "▼"}</span>}
        </div>
        <div className="text-right">
          <span className="text-[10px] text-[#9CA3AF] overline block">Last</span>
          <span className="text-[9px] text-[#6B7280] mono" data-testid="orderbook-mid">Mid {mid > 0 ? fmt(mid, mid) : "—"}</span>
        </div>
      </div>

      {/* Bids */}
      <div className="flex-1 overflow-hidden">
        {(book.bids || []).slice(0, 12).map(([price, qty], i) => {
          const sum = (book.bids || []).slice(0, i + 1).reduce((a, [, q]) => a + q, 0);
          const w = (qty / maxBid) * 100;
          return (
            <div key={`bid-${i}`} className="relative grid grid-cols-3 px-3 py-0.5 text-[11px] mono">
              <div className="depth-bar-buy absolute inset-y-0 right-0" style={{ width: `${w}%` }} />
              <span className="relative text-[#0ECB81]">{fmt(price, price)}</span>
              <span className="relative text-right">{qty.toFixed(4)}</span>
              <span className="relative text-right text-[#9CA3AF]">{sum.toFixed(3)}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
